import { Platform } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { symbolName } from '../../../components/ui/Icon'
import { LanguageIcon } from '../../../components/ui/LanguageIcon'
import { PullToRefreshScrollView } from '../../../components/ui/PullToRefreshScrollView'
import { RemoveAccountIcon } from '../../../components/ui/RemoveAccountIcon'
import { colors } from '../../../theme'
import { LegalLinksCard } from './LegalLinksCard'
import { ProfileSettingsCard } from './ProfileSettingsCard'
import { SettingsCard, SettingsRow } from './SettingsPrimitives'
import { SettingsVersionFooter } from './SettingsVersionFooter'
import { SubscriptionSettingsCard } from './SubscriptionSettingsCard'

type SettingsContentProps = {
  bottomInset: number
  onRefresh: () => void
  avatarInitial: string
  displayName: string
  userEmail: string
  onOpenEditName: () => void
  onResendVerification: () => void
  onLogout: () => void
  currentLanguageLabel: string
  onLanguageChange: () => void
  currentVoiceTypeLabel: string
  onVoiceTypeChange: () => void
  expirationText: string
  onManageSubscription: () => void
  onRestorePurchase: () => void
  termsOfServiceText: string
  privacyPolicyText: string
  onOpenTermsOfService: () => void
  onOpenPrivacyPolicy: () => void
  onReportIssue: () => void
  onDeleteAccount: () => void
  status: {
    hasActiveSubscription: boolean
    hasSentVerificationEmail: boolean
    isAssertingValidUserEmail: boolean
    isCustomerInfoPending: boolean
    isEmailVerified: boolean | undefined
    isLogoutPending: boolean
    isManagingSubscription: boolean
    isPendingEligibility: boolean
    isRefreshing: boolean
    isResendVerificationPending: boolean
    isRestorePending: boolean
    isSubscriptionCancelled: boolean
  }
}

export function SettingsContent({
  bottomInset,
  onRefresh,
  avatarInitial,
  displayName,
  userEmail,
  onOpenEditName,
  onResendVerification,
  onLogout,
  currentLanguageLabel,
  onLanguageChange,
  currentVoiceTypeLabel,
  onVoiceTypeChange,
  expirationText,
  onManageSubscription,
  onRestorePurchase,
  termsOfServiceText,
  privacyPolicyText,
  onOpenTermsOfService,
  onOpenPrivacyPolicy,
  onReportIssue,
  onDeleteAccount,
  status,
}: SettingsContentProps) {
  const {
    hasActiveSubscription,
    hasSentVerificationEmail,
    isAssertingValidUserEmail,
    isCustomerInfoPending,
    isEmailVerified,
    isLogoutPending,
    isManagingSubscription,
    isPendingEligibility,
    isRefreshing,
    isResendVerificationPending,
    isRestorePending,
    isSubscriptionCancelled,
  } = status
  const { t } = useTranslation(['profile', 'common', 'onboarding'])
  const { push } = useRouter()
  const subscriptionSubtitle = isSubscriptionCancelled
    ? expirationText
    : Platform.OS === 'ios'
      ? t('profile:subscription_link_hint_ios')
      : Platform.OS === 'android'
        ? t('profile:subscription_link_hint_android')
        : t('profile:subscription_link_hint_web')

  return (
    <PullToRefreshScrollView
      refreshing={isRefreshing}
      onRefresh={onRefresh}
      refreshControlProps={{
        colors: [colors.primary],
        tintColor: colors.primary,
      }}
      className="flex-1"
      scrollEventThrottle={16}
      contentContainerStyle={{
        paddingTop: 4,
        paddingHorizontal: 16,
        paddingBottom: 32 + bottomInset,
      }}
      showsVerticalScrollIndicator={false}
    >
      <ProfileSettingsCard
        avatarInitial={avatarInitial}
        displayName={displayName}
        userEmail={userEmail}
        isEmailVerified={isEmailVerified}
        onOpenEditName={onOpenEditName}
        onResendVerification={onResendVerification}
        isResendVerificationPending={isResendVerificationPending}
        isAssertingValidUserEmail={isAssertingValidUserEmail}
        hasSentVerificationEmail={hasSentVerificationEmail}
        onLogout={onLogout}
        isLogoutPending={isLogoutPending}
      />

      <SettingsCard className="mb-6">
        <SettingsRow
          leadingIcon={<LanguageIcon />}
          label={t('profile:language')}
          value={currentLanguageLabel}
          onPress={onLanguageChange}
          accessory="dropdown"
        />
        <SettingsRow
          icon={symbolName('music.note', 'music_note')}
          label={t('onboarding:post_payment.voice_type')}
          value={currentVoiceTypeLabel}
          onPress={onVoiceTypeChange}
          accessory="dropdown"
          isLast
        />
      </SettingsCard>

      <SubscriptionSettingsCard
        isCustomerInfoPending={isCustomerInfoPending}
        hasActiveSubscription={hasActiveSubscription}
        isSubscriptionCancelled={isSubscriptionCancelled}
        subscriptionSubtitle={subscriptionSubtitle}
        onManageSubscription={onManageSubscription}
        isManagingSubscription={isManagingSubscription}
        onSubscribe={() => push('/purchase')}
        onRestorePurchase={onRestorePurchase}
        isRestorePending={isRestorePending}
      />

      <LegalLinksCard
        termsOfServiceText={termsOfServiceText}
        privacyPolicyText={privacyPolicyText}
        onOpenTermsOfService={onOpenTermsOfService}
        onOpenPrivacyPolicy={onOpenPrivacyPolicy}
      />

      <SettingsCard className="mb-6">
        <SettingsRow
          icon={symbolName('exclamationmark.bubble', 'report_problem')}
          label={t('profile:report_issue_settings_label')}
          subtitle={t('profile:report_issue_settings_hint')}
          onPress={onReportIssue}
          isLast
        />
      </SettingsCard>

      <SettingsCard className="mb-6">
        <SettingsRow
          leadingIcon={<RemoveAccountIcon />}
          label={t('profile:remove_account')}
          onPress={onDeleteAccount}
          isLoading={isPendingEligibility}
          isDestructive
          isLast
        />
      </SettingsCard>

      <SettingsVersionFooter />
    </PullToRefreshScrollView>
  )
}
